import React, { useState } from 'react';
import { Zap, Copy, RefreshCw, Download } from 'lucide-react';
import { useToast } from '../hooks/use-toast';
import axios from 'axios';
import SEO from '../components/SEO';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Generator = () => {
  const [bin, setBin] = useState('');
  const [quantity, setQuantity] = useState(10);
  const [month, setMonth] = useState('');
  const [year, setYear] = useState('');
  const [cvv, setCvv] = useState('');
  const [generatedCards, setGeneratedCards] = useState([]);
  const [isGenerating, setIsGenerating] = useState(false);
  const { toast } = useToast();

  const handleGenerate = async () => {
    if (!bin || bin.length < 6) {
      toast({
        title: 'Error',
        description: 'Please enter a valid BIN (minimum 6 digits)',
        variant: 'destructive'
      });
      return;
    }

    setIsGenerating(true);
    
    try {
      const response = await axios.post(`${API}/generator/generate`, {
        bin: bin,
        quantity: quantity,
        month: month || null,
        year: year || null,
        cvv: cvv || null
      });
      
      const cards = response.data.map(card => card.formatted);
      setGeneratedCards(cards);
      
      toast({
        title: 'Success!',
        description: `${quantity} cards generated successfully.`,
      });
    } catch (error) {
      toast({
        title: 'Error',
        description: error.response?.data?.detail || 'Error generating cards',
        variant: 'destructive'
      });
    } finally {
      setIsGenerating(false);
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast({
      title: 'Copied!',
      description: 'Card copied to clipboard.',
    });
  };

  const copyAll = () => {
    navigator.clipboard.writeText(generatedCards.join('\n'));
    toast({
      title: 'Copied!',
      description: 'All cards copied to clipboard.',
    });
  };

  const downloadCards = () => {
    const element = document.createElement('a');
    const file = new Blob([generatedCards.join('\n')], { type: 'text/plain' });
    element.href = URL.createObjectURL(file);
    element.download = 'cards.txt';
    document.body.appendChild(element);
    element.click();
    document.body.removeChild(element);
    toast({
      title: 'Download started!',
      description: 'Cards are being downloaded.',
    });
  };

  return (
    <>
      <SEO 
        title="BIN Generator - Generate Valid Credit Cards | GENMOZPRO"
        description="Professional BIN card generator with Luhn algorithm validation. Generate up to 200 valid test credit cards with custom BIN, month, year, and CVV. Perfect for payment integration testing."
        keywords="bin generator, credit card generator, luhn algorithm, test cards, payment testing, card number generator, valid credit cards, developer tools"
        canonical="https://genmozpro.com/gen"
      />
      <div className="min-h-screen bg-black text-white pt-24 pb-12 px-4">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="text-center mb-8 sm:mb-12">
          <div className="inline-flex items-center space-x-2 px-3 sm:px-4 py-2 bg-orange-500/10 border border-orange-500/20 rounded-full mb-4 sm:mb-6">
            <Zap className="w-3 sm:w-4 h-3 sm:h-4 text-orange-500" />
            <span className="text-orange-500 text-xs sm:text-sm font-medium">BIN Generator</span>
          </div>
          <h1 className="text-3xl sm:text-4xl md:text-5xl font-bold mb-3 sm:mb-4">
            Card <span className="text-orange-500">Generator</span>
          </h1>
          <p className="text-gray-400 text-sm sm:text-base lg:text-lg px-4">
            Generate valid card numbers with Luhn algorithm for testing
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 sm:gap-8">
          {/* Input Form */}
          <div className="bg-gray-900 border border-gray-800 rounded-xl p-6 sm:p-8">
            <h2 className="text-xl sm:text-2xl font-bold mb-4 sm:mb-6">Configuration</h2>
            
            <div className="space-y-4 sm:space-y-6">
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  BIN (Bank Identification Number)
                </label>
                <input
                  type="text"
                  value={bin}
                  onChange={(e) => setBin(e.target.value.replace(/\D/g, ''))}
                  placeholder="Ex: 411111"
                  className="w-full px-4 py-3 bg-black border border-gray-700 rounded-lg focus:outline-none focus:border-orange-500 text-white transition-colors text-sm sm:text-base"
                  maxLength="16"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Quantity
                </label>
                <select
                  value={quantity}
                  onChange={(e) => setQuantity(parseInt(e.target.value))}
                  className="w-full px-4 py-3 bg-black border border-gray-700 rounded-lg focus:outline-none focus:border-orange-500 text-white transition-colors text-sm sm:text-base appearance-none cursor-pointer"
                >
                  <option value={10}>10 cards</option>
                  <option value={20}>20 cards</option>
                  <option value={50}>50 cards</option>
                  <option value={100}>100 cards</option>
                  <option value={200}>200 cards</option>
                </select>
              </div>

              <div className="grid grid-cols-2 gap-3 sm:gap-4">
                <div>
                  <label className="block text-xs sm:text-sm font-medium text-gray-300 mb-2">
                    Month
                  </label>
                  <select
                    value={month}
                    onChange={(e) => setMonth(e.target.value)}
                    className="w-full px-2 sm:px-4 py-3 bg-black border border-gray-700 rounded-lg focus:outline-none focus:border-orange-500 text-white transition-colors text-sm sm:text-base cursor-pointer"
                  >
                    <option value="">Random</option>
                    {Array.from({ length: 12 }, (_, i) => i + 1).map(m => (
                      <option key={m} value={String(m).padStart(2, '0')}>
                        {String(m).padStart(2, '0')}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-xs sm:text-sm font-medium text-gray-300 mb-2">
                    Year
                  </label>
                  <select
                    value={year}
                    onChange={(e) => setYear(e.target.value)}
                    className="w-full px-2 sm:px-4 py-3 bg-black border border-gray-700 rounded-lg focus:outline-none focus:border-orange-500 text-white transition-colors text-sm sm:text-base cursor-pointer"
                  >
                    <option value="">Random</option>
                    {Array.from({ length: 10 }, (_, i) => new Date().getFullYear() - 2000 + i).map(y => (
                      <option key={y} value={String(y).padStart(2, '0')}>
                        {String(y).padStart(2, '0')}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <button
                onClick={handleGenerate}
                disabled={isGenerating}
                className="w-full flex items-center justify-center space-x-2 px-6 py-3 sm:py-4 bg-orange-500 text-white font-semibold rounded-lg hover:bg-orange-400 transition-all disabled:opacity-50 disabled:cursor-not-allowed text-sm sm:text-base"
              >
                {isGenerating ? (
                  <>
                    <RefreshCw className="w-4 sm:w-5 h-4 sm:h-5 animate-spin" />
                    <span>Generating...</span>
                  </>
                ) : (
                  <>
                    <Zap className="w-4 sm:w-5 h-4 sm:h-5" />
                    <span>Generate Cards</span>
                  </>
                )}
              </button>
            </div>
          </div>

          {/* Results */}
          <div className="bg-gray-900 border border-gray-800 rounded-xl p-6 sm:p-8">
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between mb-4 sm:mb-6 gap-3">
              <h2 className="text-xl sm:text-2xl font-bold">Results</h2>
              {generatedCards.length > 0 && (
                <div className="flex space-x-2 w-full sm:w-auto">
                  <button
                    onClick={copyAll}
                    className="flex-1 sm:flex-none flex items-center justify-center space-x-2 px-3 sm:px-4 py-2 bg-gray-800 hover:bg-gray-700 rounded-lg transition-colors"
                  >
                    <Copy className="w-3 sm:w-4 h-3 sm:h-4" />
                    <span className="text-xs sm:text-sm">Copy All</span>
                  </button>
                  <button
                    onClick={downloadCards}
                    className="flex-1 sm:flex-none flex items-center justify-center space-x-2 px-3 sm:px-4 py-2 bg-gray-800 hover:bg-gray-700 rounded-lg transition-colors"
                  >
                    <Download className="w-3 sm:w-4 h-3 sm:h-4" />
                    <span className="text-xs sm:text-sm">Download</span>
                  </button>
                </div>
              )}
            </div>

            <div className="space-y-2 max-h-[400px] sm:max-h-[500px] overflow-y-auto">
              {generatedCards.length === 0 ? (
                <div className="text-center text-gray-500 py-12">
                  <Zap className="w-12 h-12 mx-auto mb-4 opacity-20" />
                  <p className="text-sm sm:text-base">Generated cards will appear here</p>
                </div>
              ) : (
                generatedCards.map((card, index) => (
                  <div
                    key={index}
                    className="flex items-center justify-between p-3 bg-black border border-gray-700 rounded-lg hover:border-orange-500 transition-colors group"
                  >
                    <code className="text-xs sm:text-sm text-gray-300 font-mono break-all pr-2">{card}</code>
                    <button
                      onClick={() => copyToClipboard(card)}
                      className="opacity-0 group-hover:opacity-100 text-orange-500 hover:text-orange-400 transition-all flex-shrink-0"
                    >
                      <Copy className="w-3 sm:w-4 h-3 sm:h-4" />
                    </button>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Information Section */}
      <div className="max-w-6xl mx-auto px-4 mt-12 sm:mt-16">
        <div className="bg-gradient-to-br from-gray-900 to-gray-800 border border-gray-700 rounded-2xl p-8 sm:p-12">
          <h2 className="text-3xl sm:text-4xl font-bold mb-6 text-center">
            How <span className="text-orange-500">BIN Generator</span> Works
          </h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-12">
            <div className="space-y-6">
              <div>
                <h3 className="text-xl font-semibold mb-3 flex items-center space-x-2">
                  <div className="w-8 h-8 bg-orange-500/20 rounded-lg flex items-center justify-center">
                    <span className="text-orange-500 font-bold">1</span>
                  </div>
                  <span>Enter BIN Number</span>
                </h3>
                <p className="text-gray-400 pl-10">
                  Start by entering a 6-8 digit Bank Identification Number (BIN). This identifies the card issuer and type. Common test BINs: 411111 (Visa), 555555 (Mastercard).
                </p>
              </div>

              <div>
                <h3 className="text-xl font-semibold mb-3 flex items-center space-x-2">
                  <div className="w-8 h-8 bg-orange-500/20 rounded-lg flex items-center justify-center">
                    <span className="text-orange-500 font-bold">2</span>
                  </div>
                  <span>Select Quantity</span>
                </h3>
                <p className="text-gray-400 pl-10">
                  Choose how many cards you need (10-200). Perfect for bulk testing payment integrations, form validations, or API endpoints.
                </p>
              </div>

              <div>
                <h3 className="text-xl font-semibold mb-3 flex items-center space-x-2">
                  <div className="w-8 h-8 bg-orange-500/20 rounded-lg flex items-center justify-center">
                    <span className="text-orange-500 font-bold">3</span>
                  </div>
                  <span>Customize Details</span>
                </h3>
                <p className="text-gray-400 pl-10">
                  Optionally set specific expiration month/year for your test scenarios. Leave blank for random values within valid ranges.
                </p>
              </div>
            </div>

            <div className="space-y-6">
              <div>
                <h3 className="text-xl font-semibold mb-3 flex items-center space-x-2">
                  <div className="w-8 h-8 bg-orange-500/20 rounded-lg flex items-center justify-center">
                    <span className="text-orange-500 font-bold">4</span>
                  </div>
                  <span>Luhn Algorithm Magic</span>
                </h3>
                <p className="text-gray-400 pl-10">
                  Our system applies the industry-standard Luhn algorithm to generate valid card numbers. Each number passes checksum validation just like real cards.
                </p>
              </div>

              <div>
                <h3 className="text-xl font-semibold mb-3 flex items-center space-x-2">
                  <div className="w-8 h-8 bg-orange-500/20 rounded-lg flex items-center justify-center">
                    <span className="text-orange-500 font-bold">5</span>
                  </div>
                  <span>Export & Use</span>
                </h3>
                <p className="text-gray-400 pl-10">
                  Copy individual cards or download all as a text file. Format: CARD|MM|YY|CVV for easy integration with your testing tools.
                </p>
              </div>

              <div className="bg-orange-500/10 border border-orange-500/20 rounded-lg p-4">
                <p className="text-sm text-orange-200">
                  <strong>Pro Tip:</strong> Generated cards work with most payment form validators but will not process real transactions. Perfect for frontend testing!
                </p>
              </div>
            </div>
          </div>

          <div className="border-t border-gray-700 pt-8">
            <h3 className="text-2xl font-bold mb-6 text-center">Common Use Cases</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              <div className="bg-black/50 rounded-lg p-4 border border-gray-700">
                <h4 className="font-semibold mb-2 text-orange-400">Frontend Testing</h4>
                <p className="text-sm text-gray-400">Test payment forms, validation logic, and user flows without real cards.</p>
              </div>
              <div className="bg-black/50 rounded-lg p-4 border border-gray-700">
                <h4 className="font-semibold mb-2 text-orange-400">API Integration</h4>
                <p className="text-sm text-gray-400">Validate API endpoints that accept card data in sandbox environments.</p>
              </div>
              <div className="bg-black/50 rounded-lg p-4 border border-gray-700">
                <h4 className="font-semibold mb-2 text-orange-400">QA Automation</h4>
                <p className="text-sm text-gray-400">Generate test datasets for automated testing suites and CI/CD pipelines.</p>
              </div>
              <div className="bg-black/50 rounded-lg p-4 border border-gray-700">
                <h4 className="font-semibold mb-2 text-orange-400">Education</h4>
                <p className="text-sm text-gray-400">Learn how payment systems work, understand BINs, and study the Luhn algorithm.</p>
              </div>
            </div>
          </div>

          <div className="mt-8 bg-black/50 rounded-lg p-6 border border-gray-700">
            <h3 className="text-xl font-bold mb-4">Example Generated Card</h3>
            <div className="bg-gray-900 rounded-lg p-4 font-mono text-sm">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <p className="text-gray-500 mb-1">Full Format:</p>
                  <p className="text-yellow-500">4111111111111111|12|26|123</p>
                </div>
                <div>
                  <p className="text-gray-500 mb-1">Breakdown:</p>
                  <p className="text-white">Card: <span className="text-cyan-400">4111111111111111</span></p>
                  <p className="text-white">Exp: <span className="text-green-400">12/26</span> | CVV: <span className="text-purple-400">123</span></p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      </div>
    </>
  );
};

export default Generator;
